/**
 * Stock Info Card Component
 * 
 * Displays comprehensive stock information including:
 * - Available quantity
 * - Reserved quantity
 * - Stock levels and warnings
 * - Batch/Lot information (if enabled)
 * - Serial numbers (if enabled)
 * - Location capacity (if enabled)
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  AlertTriangle, 
  CheckCircle2, 
  Info, 
  Package, 
  TrendingDown,
  TrendingUp,
  Calendar,
  Hash,
  Layers
} from "lucide-react";
import { cn } from "@/lib/utils";
import { isFeatureEnabled } from "@/config/feature-flags";
import {
  getStockLevel,
  getBatchLotsForLocation,
  getSerialNumbersForLocation,
  getLocationCapacity,
  hasCapacityForQuantity,
  type StockLevel,
  type BatchLot,
  type SerialNumber,
  type LocationCapacity,
} from "@/data/mock-stock-levels";

interface StockInfoCardProps {
  stockCode: string;
  warehouseCode: string;
  locationCode: string;
  requestedQuantity?: number;
  className?: string;
}

export function StockInfoCard({
  stockCode,
  warehouseCode,
  locationCode,
  requestedQuantity,
  className,
}: StockInfoCardProps) {
  // Get stock level data
  const stockLevel = getStockLevel(stockCode, warehouseCode, locationCode);
  
  if (!stockLevel) {
    return (
      <Alert className={className}>
        <Info className="h-4 w-4" />
        <AlertDescription>
          No stock information available for this location.
        </AlertDescription>
      </Alert>
    );
  }

  // Check if requested quantity is valid
  const hasRequestedQuantity = requestedQuantity !== undefined && requestedQuantity > 0;
  const canFulfill = hasRequestedQuantity 
    ? requestedQuantity <= stockLevel.quantityAvailable 
    : true;
  const exceedsAvailable = hasRequestedQuantity && requestedQuantity > stockLevel.quantityAvailable;
  const belowMinimum = hasRequestedQuantity 
    ? (stockLevel.quantityAvailable - requestedQuantity) < stockLevel.minimumLevel
    : stockLevel.quantityAvailable < stockLevel.minimumLevel;

  // Get batch/lot information if enabled
  const batchLots = isFeatureEnabled('enableBatchLotTracking')
    ? getBatchLotsForLocation(stockCode, warehouseCode, locationCode)
    : [];

  // Get serial numbers if enabled
  const serialNumbers = isFeatureEnabled('enableSerialNumberTracking')
    ? getSerialNumbersForLocation(stockCode, warehouseCode, locationCode, 'available')
    : [];

  // Get location capacity if enabled
  const locationCapacity = isFeatureEnabled('enableLocationCapacityLimits')
    ? getLocationCapacity(warehouseCode, locationCode)
    : undefined;

  return (
    <Card className={cn("", className)}>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Package className="h-4 w-4" />
          Stock Information
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stock Quantities */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-muted-foreground mb-1">On Hand</div>
            <div className="text-2xl font-bold">{stockLevel.quantityOnHand}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Available</div>
            <div className="text-2xl font-bold text-green-600">
              {stockLevel.quantityAvailable}
            </div>
          </div>
        </div>

        {/* Reserved Stock */}
        {isFeatureEnabled('enableStockReservations') && stockLevel.quantityReserved > 0 && (
          <div className="flex items-center justify-between p-2 bg-amber-50 rounded-md border border-amber-200">
            <span className="text-sm text-amber-900">Reserved</span>
            <Badge variant="outline" className="bg-white">
              {stockLevel.quantityReserved} units
            </Badge>
          </div>
        )}

        {/* Requested Quantity Validation */}
        {hasRequestedQuantity && (
          <div className={cn(
            "flex items-start gap-2 p-3 rounded-md border",
            canFulfill 
              ? "bg-green-50 border-green-200" 
              : "bg-red-50 border-red-200"
          )}>
            {canFulfill ? (
              <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5" />
            )}
            <div className="flex-1">
              <div className={cn(
                "text-sm font-medium",
                canFulfill ? "text-green-900" : "text-red-900"
              )}>
                {canFulfill ? "Sufficient Stock" : "Insufficient Stock"}
              </div>
              <div className={cn(
                "text-xs mt-0.5",
                canFulfill ? "text-green-700" : "text-red-700"
              )}>
                {canFulfill 
                  ? `${requestedQuantity} units can be transferred`
                  : `Only ${stockLevel.quantityAvailable} units available (need ${requestedQuantity})`
                }
              </div>
            </div>
          </div>
        )}

        {/* Stock Level Warnings */}
        {belowMinimum && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              {hasRequestedQuantity 
                ? `Transfer will drop stock below minimum level (${stockLevel.minimumLevel} units)`
                : `Stock is below minimum level (${stockLevel.minimumLevel} units)`
              }
            </AlertDescription>
          </Alert>
        )}

        {/* Stock Levels */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground flex items-center gap-1">
              <TrendingDown className="h-3 w-3" />
              Minimum Level
            </span>
            <span className="font-medium">{stockLevel.minimumLevel} units</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />
              Maximum Level
            </span>
            <span className="font-medium">{stockLevel.maximumLevel} units</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              Last Movement
            </span>
            <span className="font-medium">
              {new Date(stockLevel.lastMovementDate).toLocaleDateString()}
            </span>
          </div>
        </div>

        {/* Stock Valuation */}
        {isFeatureEnabled('enableStockValuation') && (
          <div className="pt-3 border-t">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-muted-foreground">Unit Cost</span>
              <span className="font-medium">£{stockLevel.unitCost.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">Total Value</span>
              <span className="font-bold">£{stockLevel.totalValue.toFixed(2)}</span>
            </div>
          </div>
        )}

        {/* Batch/Lot Information */}
        {isFeatureEnabled('enableBatchLotTracking') && batchLots.length > 0 && (
          <div className="pt-3 border-t space-y-2">
            <div className="text-xs font-medium flex items-center gap-1">
              <Layers className="h-3 w-3" />
              Batch/Lot Numbers ({batchLots.length})
            </div>
            <div className="space-y-1">
              {batchLots.map((batch) => (
                <div 
                  key={batch.batchNumber}
                  className="flex items-center justify-between p-2 bg-muted rounded text-xs"
                >
                  <div>
                    <div className="font-medium">{batch.batchNumber}</div>
                    {batch.expiryDate && (
                      <div className="text-muted-foreground text-[10px]">
                        Exp: {new Date(batch.expiryDate).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  <Badge variant="secondary" className="text-[10px]">
                    {batch.quantity} units
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Serial Numbers */}
        {isFeatureEnabled('enableSerialNumberTracking') && serialNumbers.length > 0 && (
          <div className="pt-3 border-t space-y-2">
            <div className="text-xs font-medium flex items-center gap-1">
              <Hash className="h-3 w-3" />
              Available Serial Numbers ({serialNumbers.length})
            </div>
            <div className="max-h-32 overflow-y-auto space-y-1">
              {serialNumbers.map((serial) => (
                <div 
                  key={serial.serialNumber}
                  className="p-2 bg-muted rounded text-xs font-mono"
                >
                  {serial.serialNumber}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Location Capacity */}
        {isFeatureEnabled('enableLocationCapacityLimits') && locationCapacity && (
          <div className="pt-3 border-t">
            <div className="text-xs font-medium mb-2">Location Capacity</div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Current / Max</span>
                <span className="font-medium">
                  {locationCapacity.currentUtilization} / {locationCapacity.maxCapacity} {locationCapacity.unit}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className={cn(
                    "h-2 rounded-full transition-all",
                    locationCapacity.currentUtilization / locationCapacity.maxCapacity > 0.9
                      ? "bg-red-500"
                      : locationCapacity.currentUtilization / locationCapacity.maxCapacity > 0.7
                      ? "bg-amber-500"
                      : "bg-green-500"
                  )}
                  style={{ 
                    width: `${Math.min((locationCapacity.currentUtilization / locationCapacity.maxCapacity) * 100, 100)}%` 
                  }}
                />
              </div>
              <div className="text-[10px] text-muted-foreground">
                {((locationCapacity.currentUtilization / locationCapacity.maxCapacity) * 100).toFixed(1)}% utilized
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Compact version for inline display in tables
 */
interface StockInfoInlineProps {
  stockCode: string;
  warehouseCode: string;
  locationCode: string;
  requestedQuantity?: number;
  className?: string;
}

export function StockInfoInline({
  stockCode,
  warehouseCode,
  locationCode,
  requestedQuantity,
  className,
}: StockInfoInlineProps) {
  const stockLevel = getStockLevel(stockCode, warehouseCode, locationCode);
  
  if (!stockLevel) {
    return (
      <div className={cn("text-xs text-muted-foreground", className)}>
        No stock data
      </div>
    );
  }

  const hasRequestedQuantity = requestedQuantity !== undefined && requestedQuantity > 0;
  const canFulfill = hasRequestedQuantity 
    ? requestedQuantity <= stockLevel.quantityAvailable 
    : true;

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <div className="flex items-center gap-1">
        <span className="text-xs text-muted-foreground">Available:</span>
        <span className={cn(
          "text-sm font-semibold",
          hasRequestedQuantity && !canFulfill && "text-red-600"
        )}>
          {stockLevel.quantityAvailable}
        </span>
      </div>
      {hasRequestedQuantity && (
        canFulfill ? (
          <CheckCircle2 className="h-4 w-4 text-green-600" />
        ) : (
          <AlertTriangle className="h-4 w-4 text-red-600" />
        )
      )}
    </div>
  );
}
